import React, { useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { FaPlus, FaEdit, FaTrash, FaEye, FaTimes, FaSave, FaTruck } from 'react-icons/fa';
import LedgerModal from '../../components/common/LedgerModal';
import api from '../../services/api';
import Loading from '../../components/common/Loading';
import './Vendors.css';

const Vendors = () => {
  const queryClient = useQueryClient();
  const [vendors, setVendors] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [viewingVendor, setViewingVendor] = useState(null);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    contact: {
      email: '',
      phone: '',
      website: ''
    },
    assignedTo: '',
    status: 'active'
  });

  useEffect(() => {
    loadVendors();
    loadUsers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const loadVendors = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/vendors');
      setVendors(response.data.vendors || []);
    } catch (err) {
      console.error('Error loading vendors:', err);
      setError('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      setUsersError('');
      console.log('[Vendors] Loading users...');
      // Request all users with a high limit to get complete list for dropdown
      const response = await api.get('/api/admin/users', {
        params: {
          limit: 1000, // High limit to get all users
          page: 1
        }
      });
      console.log('[Vendors] Users response:', response.data);
      const usersList = response.data?.users || [];
      console.log('[Vendors] Users list:', usersList);
      if (usersList.length === 0) {
        console.warn('[Vendors] No users found in response');
        setUsersError('No users found in the system. Please create users first.');
      } else {
        console.log(`[Vendors] Successfully loaded ${usersList.length} users`);
        setUsersError(''); // Clear any previous errors
      }
      setUsers(usersList);
    } catch (err) {
      console.error('[Vendors] Error loading users:', err);
      console.error('[Vendors] Error response:', err.response?.data);
      console.error('[Vendors] Error status:', err.response?.status);
      console.error('[Vendors] Error message:', err.message);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load users. Please try again.';
      setUsersError(`Failed to load users: ${errorMessage}`);
      setUsers([]); // Ensure users is set to empty array on error
    } finally {
      setUsersLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this vendor?')) {
      try {
        await api.delete(`/api/vendors/${id}`);
        setVendors(vendors.filter(v => v._id !== id));
        // Invalidate dashboard query to update vendor count
        queryClient.invalidateQueries('adminDashboard');
      } catch (err) {
        console.error('Error deleting vendor:', err);
        setError('Failed to delete vendor');
      }
    }
  };

  const openAddModal = () => {
    setEditingVendor(null);
    setFormData({
      name: '',
      description: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },
      contact: {
        email: '',
        phone: '',
        website: ''
      },
      assignedTo: '',
      status: 'active'
    });
    setShowModal(true);
    setError('');
    // Reload users when opening modal to ensure fresh data
    if (users.length === 0) {
      loadUsers();
    }
  };

  const openEditModal = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      name: vendor.name || '',
      description: vendor.description || '',
      address: {
        street: vendor.address?.street || '',
        city: vendor.address?.city || '',
        state: vendor.address?.state || '',
        zipCode: vendor.address?.zipCode || '',
        country: vendor.address?.country || ''
      },
      contact: {
        email: vendor.contact?.email || '',
        phone: vendor.contact?.phone || '',
        website: vendor.contact?.website || ''
      },
      assignedTo: vendor.assignedTo?._id || vendor.assignedTo || '',
      status: vendor.status || 'active'
    });
    setShowModal(true);
    setError('');
    // Reload users when opening modal to ensure fresh data
    if (users.length === 0) {
      loadUsers();
    }
  };

  const openViewModal = (vendor) => {
    setViewingVendor(vendor);
  };

  const openLedgerForVendor = () => {
    setShowLedgerModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingVendor(null);
    setViewingVendor(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.name || !formData.contact.email) {
        throw new Error('Please fill in required fields (Name, Email)');
      }

      if (editingVendor) {
        const response = await api.put(`/api/vendors/${editingVendor._id}`, formData);
        setVendors(vendors.map(v => v._id === editingVendor._id ? response.data.vendor : v));
      } else {
        const response = await api.post('/api/vendors', formData);
        setVendors([...vendors, response.data.vendor]);
      }

      // Invalidate dashboard query to update vendor count immediately
      queryClient.invalidateQueries('adminDashboard');

      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save vendor');
    } finally {
      setLoading(false);
    }
  };

  const filteredVendors = vendors.filter(v =>
    v.name.toLowerCase().includes(search.toLowerCase()) ||
    ((v.contact?.email || v.contactEmail) && (v.contact?.email || v.contactEmail).toLowerCase().includes(search.toLowerCase())) ||
    (v.contactPerson && v.contactPerson.toLowerCase().includes(search.toLowerCase())) ||
    (v.assignedTo?.name && v.assignedTo.name.toLowerCase().includes(search.toLowerCase()))
  );

  const totalPages = Math.max(1, Math.ceil(filteredVendors.length / itemsPerPage));
  const pageStart = (currentPage - 1) * itemsPerPage;
  const pageEnd = pageStart + itemsPerPage;
  const paginatedVendors = filteredVendors.slice(pageStart, pageEnd);

  if (loading && vendors.length === 0) return <Loading />;

  return (
    <div className="vendors-container">
      <div className="vendors-header">
        <div>
          <h1 className="vendors-title">Vendor Management</h1>
          <p className="vendors-subtitle">Create, edit, and manage vendors</p>
        </div>
        <button className="add-vendor-btn" onClick={openAddModal}>
          <FaPlus /> Add New Vendor
        </button>
      </div>

      <input
        type="text"
        placeholder="Search vendors by name, email, or assigned user..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="search-input"
      />

      <div className="vendors-table-wrapper">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
            Showing {filteredVendors.length === 0 ? 0 : (pageStart + 1)}-
            {Math.min(filteredVendors.length, pageEnd)} of {filteredVendors.length}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
            <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>Page {currentPage} of {totalPages}</span>
            <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>Next</button>
          </div>
        </div>
        <table className="vendors-table">
          <thead>
            <tr>
              <th>Vendor Name</th>
              <th>Contact Person</th>
              <th>Email</th>
              <th>Phone</th>

              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredVendors.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center' }}>No vendors found.</td>
              </tr>
            ) : (
              paginatedVendors.map(vendor => (
                <tr key={vendor._id}>
                  <td>
                    <div className="vendor-name-cell">
                      <FaTruck className="vendor-icon" />
                      <span>{vendor.name}</span>
                    </div>
                  </td>
                  <td>{vendor.contactPerson || vendor.assignedTo?.name || 'N/A'}</td>
                  <td>{vendor.contact?.email || vendor.contactEmail || 'N/A'}</td>
                  <td>{vendor.contact?.phone || 'N/A'}</td>

                  <td>
                    <span className={`status-badge ${vendor.status}`}>
                      {vendor.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons-row">
                      <button
                        onClick={() => openViewModal(vendor)}
                        className="action-btn view"
                        title="View Vendor"
                      >
                        <FaEye /> View
                      </button>
                      <button
                        onClick={() => openEditModal(vendor)}
                        className="action-btn edit"
                        title="Edit Vendor"
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(vendor._id)}
                        className="action-btn delete"
                        title="Delete Vendor"
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingVendor ? 'Edit Vendor' : 'Add New Vendor'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="vendor-form">
              <div className="form-group">
                <label htmlFor="name">Vendor Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter vendor name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter vendor description"
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contact.email">Email *</label>
                  <input
                    type="email"
                    id="contact.email"
                    name="contact.email"
                    value={formData.contact.email}
                    onChange={handleInputChange}
                    placeholder="vendor@example.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact.phone">Phone</label>
                  <input
                    type="tel"
                    id="contact.phone"
                    name="contact.phone"
                    value={formData.contact.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="contact.website">Website</label>
                <input
                  type="url"
                  id="contact.website"
                  name="contact.website"
                  value={formData.contact.website}
                  onChange={handleInputChange}
                  placeholder="https://www.vendor.com"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="address.street">Street Address</label>
                  <input
                    type="text"
                    id="address.street"
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleInputChange}
                    placeholder="123 Main St"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address.city">City</label>
                  <input
                    type="text"
                    id="address.city"
                    name="address.city"
                    value={formData.address.city}
                    onChange={handleInputChange}
                    placeholder="New York"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="address.state">State</label>
                  <input
                    type="text"
                    id="address.state"
                    name="address.state"
                    value={formData.address.state}
                    onChange={handleInputChange}
                    placeholder="NY"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address.zipCode">ZIP Code</label>
                  <input
                    type="text"
                    id="address.zipCode"
                    name="address.zipCode"
                    value={formData.address.zipCode}
                    onChange={handleInputChange}
                    placeholder="10001"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address.country">Country</label>
                <input
                  type="text"
                  id="address.country"
                  name="address.country"
                  value={formData.address.country}
                  onChange={handleInputChange}
                  placeholder="United States"
                />
              </div>

              <div className="form-row">
                {/* <div className="form-group">
                  <label htmlFor="assignedTo">Assigned To</label>
                  <select
                    id="assignedTo"
                    name="assignedTo"
                    value={formData.assignedTo}
                    onChange={handleInputChange}
                  >
                    <option value="">Select User</option>
                    {users.map(user => (
                      <option key={user._id} value={user._id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div> */}

                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={loading}
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  <FaSave /> {loading ? 'Saving...' : (editingVendor ? 'Update Vendor' : 'Add Vendor')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingVendor && (
        <div className="modal-overlay" onClick={() => setViewingVendor(null)}>
          <div className="modal-content view-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Vendor Details</h2>
              <button className="modal-close" onClick={() => setViewingVendor(null)}>
                <FaTimes />
              </button>
            </div>

            <div className="vendor-view-content">
              <div className="vendor-info-section">
                <div className="detail-item">
                  <label>Vendor Name:</label>
                  <span>{viewingVendor.name}</span>
                </div>
                <div className="detail-item">
                  <label>Description:</label>
                  <span>{viewingVendor.description || 'No description available'}</span>
                </div>
                <div className="detail-item">
                  <label>Contact Person:</label>
                  <span>{viewingVendor.contactPerson || viewingVendor.assignedTo?.name || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Email:</label>
                  <span>{viewingVendor.contact?.email || viewingVendor.contactEmail || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Phone:</label>
                  <span>{viewingVendor.contact?.phone || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Website:</label>
                  <span>{viewingVendor.contact?.website || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Assigned To:</label>
                  <span>{viewingVendor.assignedTo?.name || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Status:</label>
                  <span className={`status-badge ${viewingVendor.status}`}>
                    {viewingVendor.status}
                  </span>
                </div>
              </div>

              <div className="address-section">
                <h3>Address</h3>
                <div className="address-details">
                  <div className="detail-item">
                    <label>Street:</label>
                    <span>{viewingVendor.address?.street || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>City:</label>
                    <span>{viewingVendor.address?.city || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>State:</label>
                    <span>{viewingVendor.address?.state || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>ZIP Code:</label>
                    <span>{viewingVendor.address?.zipCode || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Country:</label>
                    <span>{viewingVendor.address?.country || 'N/A'}</span>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 12 }}>
                <button className="btn-primary" onClick={openLedgerForVendor}>
                  Add Ledger Entry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <LedgerModal
        isOpen={showLedgerModal}
        onClose={() => setShowLedgerModal(false)}
        defaultDescription={viewingVendor ? `Vendor ${viewingVendor.name} adjustment` : ''}
      />
    </div>
  );
};

export default Vendors;

